import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import type { AtvModel } from '../hooks/useAtvModels';
import type { Accessory } from '../hooks/useAccessories';
import { getSettings } from '../utils/settings';
import {
  BookingApiError, loadAtvs, loadAccessories, loadBusySnapshot, loadRentalConfig,
  loadConfiguredAvailability, loadQuote,
} from './api';
import { useNextAvailability } from './useNextAvailability';
import { calculateCatalogPrice } from './pricing';
import {
  BOOKING_TIMEZONE, BookingRange, BookingSelection, BusySnapshot, RentalOffer, RentalReturnInput,
  getBookingRange, getConfiguredRange, getDefaultReturn, getTimeOptions,
  getConfirmedAvailability, getNextBookingSlot, getReturnAfterPickupChange, parseWarsawTime,
  isUsableQuote, selectionKey, AvailabilityStatus,
} from './model';

type Remote<T> =
  | { status: 'loading'; key: string | null }
  | { status: 'ready'; key: string | null; data: T }
  | { status: 'error'; key: string | null; error: Error; retryAt: number };

function useRemote<T>(
  key: string | null, load: (signal: AbortSignal) => Promise<T>,
  revision: number, delay = 0, retain = false,
) {
  const [state, setState] = useState<Remote<T>>({ status: 'loading', key });
  const [retryRevision, setRetryRevision] = useState(0);
  useEffect(() => {
    if (state.status !== 'error' || state.retryAt <= Date.now()) return;
    const timer = setTimeout(() => setRetryRevision(value => value + 1), state.retryAt - Date.now());
    return () => clearTimeout(timer);
  }, [state]);
  useEffect(() => {
    if (key === null) return;
    if (state.status === 'error' && state.key === key && state.retryAt > Date.now()) return;
    const controller = new AbortController();
    let active = true;
    setState(previous => retain && previous.status === 'ready' && previous.key === key
      ? previous : { status: 'loading', key });
    const timer = setTimeout(() => {
      if (document.hidden) return;
      Promise.resolve().then(() => load(controller.signal)).then(
        data => { if (active) setState({ status: 'ready', key, data }); },
        error => {
          if (active && !controller.signal.aborted) {
            console.error('Booking read failed', error instanceof BookingApiError ? error.code : 'NETWORK');
            setState({ status: 'error', key,
              error: error instanceof Error ? error : new Error('Booking read failed'),
              retryAt: Date.now() + (error instanceof BookingApiError ? error.retryAfterSeconds * 1000 : 0) });
          }
        },
      );
    }, delay);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
    // Keys represent request contents; revisions trigger refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, revision, retryRevision]);
  return state;
}

export function useBooking() {
  const settings = useMemo(getSettings, []);
  const [offer, updateOffer] = useState<RentalOffer>('three-hours');
  const [date, updateDate] = useState('');
  const [time, updateTime] = useState(settings.businessHours.start);
  const [customReturn, setCustomReturn] = useState<RentalReturnInput | null>(null);
  const [vehicleId, updateVehicleId] = useState('');
  const [accessoryIds, setAccessoryIds] = useState<string[]>([]);
  const [paymentType, setPaymentType] = useState<'deposit' | 'full'>('deposit');
  const [message, setMessage] = useState('');
  const [revision, setRevision] = useState(0);
  const [quoteRevision, setQuoteRevision] = useState(0);
  const [clock, setClock] = useState(0);
  const [paused, setPaused] = useState(true);
  const [seekingSlot, setSeekingSlot] = useState(false);
  const [notice, setNotice] = useState<'adjustedPickup' | 'adjustedReturn' | 'noDates' | 'unconfirmedDates' | null>(null);
  const defaultsInitialized = useRef(false);
  const manualPickup = useRef(false);
  const manualVehicle = useRef(false);
  const scannedSlots = useRef(0);
  const availabilityRequested = useRef(false);
  const quoteRequested = useRef(false);
  const configState = useRemote('config', loadRentalConfig, revision, 0, true);
  const config = configState.status === 'ready' ? configState.data : null;
  const fleet = useRemote<AtvModel[]>('fleet', loadAtvs, revision, 0, true);
  const accessories = useRemote<Accessory[]>('accessories', loadAccessories, revision, 0, true);
  const vehicles = fleet.status === 'ready' ? fleet.data : [];
  const extras = accessories.status === 'ready' ? accessories.data : [];
  const selectedVehicle = vehicles.find(vehicle => vehicle.id === vehicleId);
  const accessoriesAllowed = config
    ? !!config.offers.find(item => item.id === offer)?.accessoriesAllowed : offer === 'day';
  const defaultReturn = useMemo(() => getDefaultReturn(offer, date, time), [offer, date, time]);
  const plannedReturn = offer === 'day' ? customReturn ?? defaultReturn : null;
  const applySlot = (slot: { date: string; time: string; range: BookingRange }, keepChosenReturn = false) => {
    updateDate(slot.date); updateTime(slot.time);
    if (keepChosenReturn) {
      const end = dayjs(slot.range.endsAt).tz(BOOKING_TIMEZONE);
      setCustomReturn({ date: end.format('YYYY-MM-DD'), time: end.format('HH:mm') });
    }
  };
  const setOffer = (value: RentalOffer) => {
    if (value === offer) return;
    updateOffer(value); setNotice(null);
    const chosenReturn = value === 'day' ? getReturnAfterPickupChange(date, time, customReturn) : undefined;
    if (value === 'day') setCustomReturn(chosenReturn ?? null);
    const start = parseWarsawTime(date, time);
    if (config && start && getConfiguredRange(value, date, time, config, Date.now(), chosenReturn ?? undefined).status !== 'ready') {
      const end = chosenReturn && parseWarsawTime(chosenReturn.date, chosenReturn.time);
      const duration = end ? (end.valueOf() - start.valueOf()) / 60_000 : undefined;
      const slot = getNextBookingSlot(value, config, Date.now(), start.valueOf() - 1, duration);
      if (slot) {
        const pickupChanged = slot.date !== date || slot.time !== time;
        applySlot(slot, !!chosenReturn); setNotice(pickupChanged ? 'adjustedPickup' : 'adjustedReturn');
      }
    }
    scannedSlots.current = 0;
    setSeekingSlot(!manualPickup.current);
  };
  const setPickup = (nextDate: string, nextTime: string) => {
    manualPickup.current = true; setSeekingSlot(false); setNotice(null);
    if (nextDate === date && nextTime === time) return;
    const preservedReturn = getReturnAfterPickupChange(nextDate, nextTime, customReturn);
    if (customReturn && !preservedReturn && offer === 'day') setNotice('adjustedReturn');
    updateDate(nextDate); updateTime(nextTime); setCustomReturn(preservedReturn);
  };
  const setDate = (value: string) => setPickup(value, time);
  const setTime = (value: string) => setPickup(date, value);
  const setVehicleId = useCallback((value: string) => {
    manualVehicle.current = true; updateVehicleId(value);
  }, []);

  useEffect(() => {
    if (!paused && !manualVehicle.current && !vehicleId && vehicles.length) updateVehicleId(vehicles[0].id);
  }, [paused, vehicleId, vehicles]);

  useEffect(() => {
    if (paused || !config || !clock || defaultsInitialized.current) return;
    defaultsInitialized.current = true;
    const enabledOffer = config.offers.find(item => item.id === offer && item.enabled)?.id
      ?? config.offers.find(item => item.enabled)?.id;
    if (!enabledOffer) { setNotice('noDates'); return; }
    updateOffer(enabledOffer);
    if (manualPickup.current) return;
    const slot = getNextBookingSlot(enabledOffer, config, Date.now());
    if (slot) { applySlot(slot); setSeekingSlot(true); }
    else setNotice('noDates');
    // Defaults initialize once, after private session recovery has finished.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, config, clock, offer]);

  useEffect(() => {
    setClock(Date.now());
    const tick = setInterval(() => setClock(Date.now()), 1000);
    const refresh = () => { if (!document.hidden) setRevision(value => value + 1); };
    const visible = () => {
      if (!document.hidden) { refresh(); setQuoteRevision(value => value + 1); }
    };
    const poll = setInterval(refresh, 30_000);
    document.addEventListener('visibilitychange', visible);
    return () => { clearInterval(tick); clearInterval(poll); document.removeEventListener('visibilitychange', visible); };
  }, []);

  const rangeResult = config ? getConfiguredRange(offer, date, time, config, clock, plannedReturn ?? undefined)
    : getBookingRange(offer, date, time, {
      ...settings.businessHours, maxDays: settings.maxReservationDays,
      minLeadMinutes: settings.minReservationOffsetHours * 60, stepMinutes: settings.minutesStep,
      lastTodayHour: settings.lastTodayReservationHour,
    }, clock, plannedReturn ?? undefined);
  const range = rangeResult.status === 'ready' ? rangeResult.range : null;
  const selection: BookingSelection | null = range && vehicleId
    ? { ...range, offer, vehicleId, accessoryIds, paymentType } : null;
  const currentKey = selection ? selectionKey(selection) : '';
  const active = !paused && !!config?.bookingEnabled && !!config.termsVersion && !!range && clock > 0;
  const availabilityRequest: Parameters<typeof loadConfiguredAvailability>[0] | null = active && range ? {
    offerId: offer, ...range, timezone: BOOKING_TIMEZONE,
    vehicleIds: vehicles.map(vehicle => vehicle.id).sort(),
    accessoryIds: accessoriesAllowed ? extras.map(extra => extra.id).sort() : [],
  } : null;
  const availabilityKey = availabilityRequest && fleet.status === 'ready'
    && (!accessoriesAllowed || accessories.status === 'ready') ? JSON.stringify(availabilityRequest) : null;
  const availabilityState = useRemote(availabilityKey, async signal => {
    if (!availabilityRequest || !config) throw new BookingApiError('INVALID_SELECTION');
    availabilityRequested.current = true;
    return loadConfiguredAvailability(availabilityRequest, config, signal);
  }, revision, seekingSlot && scannedSlots.current > 0 ? 1000 : availabilityRequested.current ? 300 : 0, true);
  const confirmedSnapshot = active && availabilityKey && availabilityState.status === 'ready'
    && availabilityState.key === availabilityKey && config
    && Date.parse(availabilityState.data.expiresAt) > clock
    && Date.parse(availabilityState.data.expiresAt) - Date.parse(availabilityState.data.checkedAt)
      <= config.availabilityTtlSeconds * 1000 ? availabilityState.data : null;
  const statusFor = (kind: 'vehicles' | 'accessories', id: string): AvailabilityStatus => {
    if (!active || !availabilityKey) return 'unknown';
    if (availabilityState.key !== availabilityKey || availabilityState.status === 'loading') return 'loading';
    if (availabilityState.status === 'error') return 'error';
    if (Date.parse(availabilityState.data.expiresAt) - Date.parse(availabilityState.data.checkedAt)
      > (config?.availabilityTtlSeconds ?? 0) * 1000) return 'unknown';
    return getConfirmedAvailability(availabilityState.data, kind, id, clock);
  };
  const availabilityFor = (id: string) => statusFor('vehicles', id);
  const accessoryAvailabilityFor = (id: string) => statusFor('accessories', id);
  const availabilityError = availabilityKey && availabilityState.key === availabilityKey && availabilityState.status === 'error'
    ? availabilityState.error : null;
  const availability = availabilityError ? 'error' : selectedVehicle ? availabilityFor(selectedVehicle.id) : 'unknown';
  const hasAvailableAlternative = !!confirmedSnapshot && vehicles.some(vehicle => vehicle.id !== vehicleId
    && getConfirmedAvailability(confirmedSnapshot, 'vehicles', vehicle.id, clock) === 'available');
  const busyIds = active && availabilityState.status === 'ready' && availabilityState.key === availabilityKey
    ? vehicles.filter(vehicle => availabilityState.data.vehicles.some(entry => entry.id === vehicle.id && entry.status === 'unavailable'))
      .map(vehicle => vehicle.id) : [];
  const busySnapshotState = useRemote<BusySnapshot>(busyIds.length ? 'busy' : null, loadBusySnapshot, revision, 0, true);
  const busySlotsByVehicle = busySnapshotState.status === 'ready'
    ? Object.fromEntries(busyIds.map(id => {
      const vehicle = vehicles.find(item => item.id === id);
      return [id, [...busySnapshotState.data.global, ...(vehicle ? busySnapshotState.data.byModel[vehicle.name] ?? [] : [])]];
    })) : null;
  const nextAvailability = useNextAvailability(active && !seekingSlot && range && busyIds.length
    && busySlotsByVehicle
    && accessoryIds.every(id => extras.some(extra => extra.id === id))
    ? { offer, range, vehicleIds: busyIds, accessoryIds: [...accessoryIds].sort(), busySlotsByVehicle } : null, config);
  const nextAvailabilityFor = (id: string) => {
    if (busySnapshotState.status === 'error' && busyIds.includes(id)) return { status: 'error' } as const;
    const result = nextAvailability.results[id];
    if (result?.status === 'available' && Date.parse(result.expiresAt) <= clock) return { status: 'searching' } as const;
    return result ?? { status: 'searching' } as const;
  };
  const useNextSlot = (id: string) => {
    const result = nextAvailability.results[id];
    if (paused || !config || result?.status !== 'available' || Date.parse(result.expiresAt) <= Date.now()) return;
    const start = dayjs(result.range.startsAt).tz(BOOKING_TIMEZONE);
    const end = dayjs(result.range.endsAt).tz(BOOKING_TIMEZONE);
    if (getConfiguredRange(offer, start.format('YYYY-MM-DD'), start.format('HH:mm'), config, Date.now(),
      offer === 'day' ? { date: end.format('YYYY-MM-DD'), time: end.format('HH:mm') } : undefined).status !== 'ready') {
      nextAvailability.retry(); return;
    }
    manualPickup.current = true; manualVehicle.current = true;
    setSeekingSlot(false); setNotice(null); updateVehicleId(id);
    applySlot({ date: start.format('YYYY-MM-DD'), time: start.format('HH:mm'), range: result.range }, offer === 'day');
  };
  useEffect(() => {
    if (paused || !config || !clock || manualPickup.current || !defaultsInitialized.current || !date) return;
    if (rangeResult.status !== 'invalid' || !['PAST', 'TOO_SOON', 'TODAY_CLOSED', 'OUTSIDE_HOURS', 'TOO_FAR', 'INVALID_STEP'].includes(rangeResult.code)) return;
    const slot = getNextBookingSlot(offer, config, Date.now());
    if (slot) {
      applySlot(slot); setCustomReturn(null); setNotice('adjustedPickup');
      scannedSlots.current = 0; setSeekingSlot(true);
    } else { setSeekingSlot(false); setNotice('noDates'); }
    // Only system-owned pickup defaults may move when calendar rules change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, config, clock, date, rangeResult.status, rangeResult.status === 'invalid' ? rangeResult.code : null, offer]);
  useEffect(() => {
    if (!active || !confirmedSnapshot || !config || !range) return;
    const candidates = manualVehicle.current ? vehicles.filter(vehicle => vehicle.id === vehicleId) : vehicles;
    const available = candidates.find(vehicle => getConfirmedAvailability(confirmedSnapshot, 'vehicles', vehicle.id, clock) === 'available');
    if (available) {
      if (!manualVehicle.current && getConfirmedAvailability(confirmedSnapshot, 'vehicles', vehicleId, clock) !== 'available') {
        updateVehicleId(available.id);
      }
      setSeekingSlot(false);
      setNotice(previous => previous === 'noDates' || previous === 'unconfirmedDates' ? null : previous);
    } else if (seekingSlot && !manualPickup.current && vehicles.length) {
      if (!candidates.length || candidates.some(vehicle => getConfirmedAvailability(confirmedSnapshot, 'vehicles', vehicle.id, clock) !== 'unavailable')) {
        setSeekingSlot(false); setNotice('unconfirmedDates'); return;
      }
      const duration = offer === 'day' && customReturn
        ? (Date.parse(range.endsAt) - Date.parse(range.startsAt)) / 60_000 : undefined;
      const slot = getNextBookingSlot(offer, config, Date.now(), Date.parse(range.startsAt), duration);
      if (slot) { scannedSlots.current++; applySlot(slot, !!customReturn && offer === 'day'); }
      else { setSeekingSlot(false); setNotice('noDates'); }
    }
    // The snapshot key prevents late availability from changing another selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, confirmedSnapshot, config, range?.startsAt, range?.endsAt, vehicles, vehicleId, seekingSlot, clock, offer]);
  const selectionValid = !!selectedVehicle && (!accessoryIds.length || accessoriesAllowed)
    && accessoryIds.every(id => extras.some(extra => extra.id === id));
  // Price and availability are independent reads; checkout still requires both fresh results.
  const quoteKey = active && availabilityKey && selection && selectionValid
    && (!seekingSlot || scannedSlots.current === 0)
    && availability !== 'unavailable' && availability !== 'error'
    && accessoryIds.every(id => !['unavailable', 'error'].includes(accessoryAvailabilityFor(id)))
    ? JSON.stringify([currentKey, config?.rulesVersion]) : null;
  const quoteState = useRemote(quoteKey, async signal => {
    if (!selection || !config) throw new BookingApiError('INVALID_SELECTION');
    quoteRequested.current = true;
    const result = await loadQuote(selection, signal);
    if (!isUsableQuote(result, selection) || result.rulesVersion !== config.rulesVersion) {
      throw new BookingApiError('INVALID_RESPONSE');
    }
    return result;
  }, quoteRevision, quoteRequested.current ? 300 : 0);
  const quote = quoteKey && quoteState.status === 'ready' && quoteState.key === quoteKey
    && selection && isUsableQuote(quoteState.data, selection, clock) ? quoteState.data : null;
  useEffect(() => {
    if (!document.hidden && quoteKey && quoteState.key === quoteKey && quoteState.status === 'ready' && Date.parse(quoteState.data.expiresAt) <= clock) {
      setQuoteRevision(value => value + 1);
    }
  }, [quoteState, clock, quoteKey]);
  useEffect(() => {
    if (!document.hidden && availabilityKey && availabilityState.key === availabilityKey && availabilityState.status === 'ready'
      && Date.parse(availabilityState.data.expiresAt) <= clock) {
      setRevision(value => value + 1);
    }
  }, [availabilityState, clock, availabilityKey]);

  const rentalPriceFor = (id: string) => {
    if (!range || fleet.status !== 'ready') return null;
    const result = calculateCatalogPrice({ ...range, offer, vehicleId: id, accessoryIds: [], paymentType: 'full' }, vehicles, []);
    return result.status === 'ready' ? result.price.rentalMinor : null;
  };
  const canContinue = active && !seekingSlot && selectionValid && !!quote && availability === 'available'
    && accessoryIds.every(id => accessoryAvailabilityFor(id) === 'available');
  const priceLoading = !!quoteKey && (quoteState.status === 'loading' || quoteState.key !== quoteKey);
  const loadingStage = configState.status === 'loading' || fleet.status === 'loading'
    || accessoriesAllowed && accessories.status === 'loading' ? 'catalog'
    : active && vehicles.length > 0 && !availabilityError && (seekingSlot || availability === 'loading') ? 'availability'
    : priceLoading ? 'quote' : null;
  const retry = () => {
    if (!manualPickup.current && notice === 'noDates' && config) {
      const slot = getNextBookingSlot(offer, config, Date.now());
      if (slot) { applySlot(slot); setNotice(null); scannedSlots.current = 0; setSeekingSlot(true); }
    } else if (!manualPickup.current && notice === 'unconfirmedDates') setSeekingSlot(true);
    setRevision(value => value + 1); setQuoteRevision(value => value + 1);
  };
  const startNewReservation = () => {
    manualPickup.current = false; manualVehicle.current = false;
    scannedSlots.current = 0; setNotice(null); setSeekingSlot(true);
    if (config && getConfiguredRange(offer, date, time, config, Date.now(), plannedReturn ?? undefined).status !== 'ready') {
      const slot = getNextBookingSlot(offer, config, Date.now());
      if (slot) { applySlot(slot); setCustomReturn(null); }
      else setNotice('noDates');
    }
    setRevision(value => value + 1); setQuoteRevision(value => value + 1);
  };
  const adjustPickup = () => {
    if (!config) return;
    const start = parseWarsawTime(date, time);
    const end = customReturn && parseWarsawTime(customReturn.date, customReturn.time);
    const duration = start && end && end.valueOf() - start.valueOf() >= 180 * 60_000
      ? (end.valueOf() - start.valueOf()) / 60_000 : undefined;
    const slot = getNextBookingSlot(offer, config, Date.now(), start ? start.valueOf() - 1 : Date.now(), duration)
      ?? getNextBookingSlot(offer, config, Date.now(), Date.now(), duration);
    if (!slot) { setNotice('noDates'); return; }
    manualPickup.current = true; setSeekingSlot(false);
    applySlot(slot, !!customReturn && duration !== undefined);
    if (customReturn && duration === undefined) setCustomReturn(null);
    setNotice('adjustedPickup'); setRevision(value => value + 1); setQuoteRevision(value => value + 1);
  };
  const confirmSelection = () => {
    manualPickup.current = true; manualVehicle.current = true; setSeekingSlot(false);
  };
  const calendar = config?.calendar;
  const restore = useCallback((value: BookingSelection & { message: string }) => {
    defaultsInitialized.current = true; manualPickup.current = true; manualVehicle.current = true;
    setSeekingSlot(false); setNotice(null);
    const start = dayjs(value.startsAt).tz(BOOKING_TIMEZONE), end = dayjs(value.endsAt).tz(BOOKING_TIMEZONE);
    updateOffer(value.offer); updateDate(start.format('YYYY-MM-DD')); updateTime(start.format('HH:mm'));
    setCustomReturn(value.offer === 'day' ? { date: end.format('YYYY-MM-DD'), time: end.format('HH:mm') } : null);
    updateVehicleId(value.vehicleId); setAccessoryIds(value.accessoryIds); setPaymentType(value.paymentType); setMessage(value.message);
  }, []);
  return {
    offer, setOffer, date, setDate, time, setTime, vehicleId, setVehicleId,
    offers: config ? config.offers.filter(item => item.enabled).map(item => item.id) : ['three-hours', 'day'] as RentalOffer[],
    returnDate: plannedReturn?.date ?? '', returnTime: plannedReturn?.time ?? '',
    billableDays: offer === 'day' && range ? Math.ceil((Date.parse(range.endsAt) - Date.parse(range.startsAt)) / 86_400_000) : 0,
    setReturnDate: (value: string) => {
      manualPickup.current = true; setSeekingSlot(false); setNotice(null);
      setCustomReturn({ date: value, time: plannedReturn?.time ?? '' });
    },
    setReturnTime: (value: string) => {
      manualPickup.current = true; setSeekingSlot(false); setNotice(null);
      setCustomReturn({ date: plannedReturn?.date ?? '', time: value });
    },
    accessoryIds, setAccessoryIds, accessoriesAllowed, paymentType, setPaymentType, message, setMessage,
    fleet, vehicles, accessories, rangeResult, range, selection, currentKey,
    config, configState, availabilityFor, accessoryAvailabilityFor, availability, availabilityError, hasAvailableAlternative, quote,
    nextAvailabilityFor, useNextSlot, retryNextAvailability: () => {
      if (busySnapshotState.status === 'error') setRevision(value => value + 1);
      else nextAvailability.retry();
    },
    searching: seekingSlot && active && fleet.status === 'ready' && vehicles.length > 0 && !availabilityError, notice,
    price: paused && quoteState.status === 'ready' && quoteState.data.selectionKey === currentKey ? quoteState.data : quote,
    rentalPriceFor, setPaused,
    priceLoading, loadingStage,
    priceError: !!quoteKey && quoteState.key === quoteKey && quoteState.status === 'error',
    quoteError: quoteState.status === 'error' ? quoteState.error : null,
    canContinue, retry, restore, startNewReservation, confirmSelection, adjustPickup,
    canAdjustPickup: !!config && !paused && rangeResult.status === 'invalid'
      && ['PAST', 'TOO_SOON', 'TODAY_CLOSED', 'OUTSIDE_HOURS', 'TOO_FAR', 'INVALID_STEP'].includes(rangeResult.code),
    today: clock ? dayjs(clock).tz(BOOKING_TIMEZONE).format('YYYY-MM-DD') : '',
    maxDate: clock ? dayjs(clock).tz(BOOKING_TIMEZONE).add(calendar?.maxDays ?? settings.maxReservationDays, 'day').format('YYYY-MM-DD') : '',
    timeOptions: getTimeOptions(calendar?.openingStart ?? settings.businessHours.start, calendar?.openingEnd ?? settings.businessHours.end),
  };
}
